import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useLocalizedNavigate } from '@app/app/i18n';
import { analytics } from '@app/app/analytics';
import { CustomerCategory } from '@app/app/analytics/types';
// import { trackAuthAnalytics } from '@app/app/analytics/register.analytics';
import { AccountConfirmNotify, ResendConfirmAccountNotify } from '@app/components/template';
import { route_path, WITHOUT_FOOTER_LOCATIONS, PROTECTED_ROUTE_LIST } from '@app/utils/route_path';
import { CommonInit, UserData, UserDataType } from '@app/types/user.type';
import { isBoxedMode, isProPlan } from '@app/utils/user-status.utils';
import {
  useGetCommonInitQuery,
  useGetUserInitQuery,
  useLogOutMutation,
} from '@app/store/api/user.api';

import { AnimatePresence, motion } from 'framer-motion';

import { Footer } from '@app/widgets/footer';
import { useAppDispatch, useAppSelector } from '@app/hooks/use-store';
import { checkAuth } from '@app/store/slice/auth.slice';
import { PaymentsDialog } from '@app/widgets/payments-dialog';
import { SettingsDialog } from '@app/widgets/settings-dialog';
import { gtmInstance } from '@app/app/analytics/analytics';
import { WelcomeMessageModal } from '@app/modals/WelcomeMessage';
import { UpgradePopup } from '@app/components/template/trial-popup/upgrade-popup';
import { UpgradePopupVariant } from '@app/components/template/trial-popup/types';
import { usePersistedAccountsStore } from '@app/app/persist';
import { useSharingDialogData } from '@app/widgets/sharing-dialog/model';
import { COOKIE_KEY, cookieUtils } from '@app/app/cookie';
import { useOAuthAnalytics } from '@app/app/analytics/register.analytics';
import { usePaywallPopup } from '@app/app/model';
import { useAnalyticsLanguage } from '@app/hooks/use-analytics-language';
import { useSessionPing } from '@app/hooks/use-session-ping';
import { setLanguageCookie } from '@app/app/i18n/utils/language-cookie';
import i18n from '@app/app/i18n/config';
import { addLanguagePrefix, removeLanguagePrefix } from '@app/app/i18n/utils/language-url';
import { POPUP_VARIANT_MAP } from '@app/components/template/trial-popup/config/upgrade-popup-config';
import { SSERecoveryDebugPanel } from '@app/widgets/sse-recovery-debug';

import { NewVersionBanner } from './new-version-banner';
// import { UpgradePopupTest } from '@app/components/template/trial-popup';

export type CommonOutletContext = {
  user_ctx?: UserData['user_ctx'];
  updateData: UserDataType;
  logout: () => void;
  context?: UserData;
  commonContext?: CommonInit;
  setEmailNotify: Dispatch<SetStateAction<boolean>>;
  setSendConfirmNotify: Dispatch<SetStateAction<boolean>>;
  setShowSettings: Dispatch<SetStateAction<boolean>>;
  clearUserCtx: () => void;
};

export const detectCustomerCategory = (userData: UserData): CustomerCategory => {
  const { product_balance } = userData;

  if (!product_balance?.product || product_balance.product.is_trial) return '';

  if (product_balance.is_active) {
    return product_balance.product.count ? 'PAYG_client' : 'subscriber';
  } else {
    return product_balance.product.count ? 'PAYG_exclient' : 'exsubscriber';
  }
};

export const CommonOutlet: FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading: isAuthCheckLoading } = useAppSelector(
    (state) => state.authState,
  );
  const [logout] = useLogOutMutation();
  const navigate = useLocalizedNavigate();
  const location = useLocation();

  const { data: commonInit, isLoading: isCommonInitLoading } = useGetCommonInitQuery(null);

  const {
    data: userInit,
    isSuccess,
    refetch,
  } = useGetUserInitQuery(null, { skip: !isAuthenticated });

  const { paywallPopupState, resetPaywallPopup } = usePaywallPopup();
  // State for welcome message modal
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [buttonText, setButtonText] = useState('');

  // Проверяем нужно ли показать уведомление о подтверждении email первый раз после регистрации
  const checkAndSetEmailNotify = useCallback(() => {
    const firstTimeEmailNotifyShown = sessionStorage.getItem('firstTimeEmailNotifyShown');
    if (
      !firstTimeEmailNotifyShown &&
      isAuthenticated &&
      !userInit?.result?.user_ctx?.is_confirmed
    ) {
      sessionStorage.setItem('firstTimeEmailNotifyShown', 'true');
      return true;
    }
    return false;
  }, [isAuthenticated, userInit]);

  const [emailNotify, setEmailNotify] = useState(false);
  const [isFirstTimeEmailNotify, setIsFirstTimeEmailNotify] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [sendConfirmNotify, setSendConfirmNotify] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [userCtx, setUserCtx] = useState<UserData['user_ctx'] | undefined>(
    userInit?.result?.user_ctx,
  );

  const authPages = ['login', 'register', 'password-reset'];
  const isAuthPage = authPages.some((page) => {
    const pathSegment = location.pathname.split('/')[1];
    return pathSegment === page;
  });

  const mode = commonInit?.result?.DEV ? 'development' : 'production';
  const isBoxed = isBoxedMode(commonInit?.result);
  const isDevMode = useMemo(() => !!commonInit?.result?.DEV, [commonInit]);

  // Диагностика: маунт/анмаунт CommonOutlet
  useEffect(() => {
    // console.debug('[CommonOutlet] MOUNT', { path: location.pathname });
    return () => {
      // console.debug('[CommonOutlet] UNMOUNT', { path: location.pathname });
    };
  }, []);

  useEffect(() => {
    const performAuthCheck = async () => {
      await dispatch(checkAuth());
      setIsAuthChecked(true);
    };

    performAuthCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Редирект с главной страницы для boxed режима (Sibur)
  // Редиректим только с главной страницы (/) на text-to-slides или регистрацию
  // Остальные страницы защищаются через BoxedRouteGuard в роутере
  useEffect(() => {
    if (isBoxed) {
      if (isAuthChecked && !isAuthCheckLoading && !isCommonInitLoading && commonInit?.result) {
        const pathWithoutLang = removeLanguagePrefix(location.pathname);
        const isMainPage = pathWithoutLang === '/' || pathWithoutLang === '';
        const firstSegment = pathWithoutLang.split('/').filter(Boolean)[0];
        const isAuthPageSegment = authPages.includes(firstSegment);

        // Редиректим только с главной страницы
        if (isMainPage) {
          if (isAuthenticated) {
            // Авторизован - редирект на text-to-slides
            const currentLanguage = i18n.language;
            const textToSlidesPath = addLanguagePrefix(route_path.textToSlides, currentLanguage);
            navigate(textToSlidesPath, { replace: true });
          } else if (!isAuthPageSegment) {
            // Не авторизован - редирект на регистрацию
            const currentLanguage = i18n.language;
            const localizedRegisterPath = addLanguagePrefix(route_path.register, currentLanguage);
            navigate(localizedRegisterPath, { replace: true });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthChecked,
    isAuthCheckLoading,
    isCommonInitLoading,
    isBoxed,
    isAuthenticated,
    location.pathname,
    navigate,
    commonInit?.result,
  ]);

  useEffect(() => {
    if (!userInit?.result) return;

    // Sentry: привязываем пользователя к событиям ошибок
    const userCtxData = userInit.result.user_ctx;
    if (userCtxData?.id) {
      Sentry.setUser({
        id: String(userCtxData.id),
        username: userCtxData.username,
      });
    }

    // Устанавливаем куку языка из профиля при первом получении данных пользователя
    // Приоритет: язык из профиля пользователя (если есть)
    const profileLang = userInit.result.lang;
    if (profileLang) {
      const currentLang = i18n.language;

      // Устанавливаем куку и язык только если они отличаются
      if (profileLang !== currentLang) {
        setLanguageCookie(profileLang, true);
        i18n.changeLanguage(profileLang);
      }
    }

    const currentCustomerCategory = cookieUtils.get(COOKIE_KEY.customerCategory);
    const detectedCustomerCategory = detectCustomerCategory(userInit?.result);
    if (
      !currentCustomerCategory ||
      currentCustomerCategory === '' ||
      (detectedCustomerCategory !== '' && currentCustomerCategory !== detectedCustomerCategory)
    ) {
      cookieUtils.set(COOKIE_KEY.customerCategory, detectedCustomerCategory);
    }
  }, [userInit?.result]);

  useEffect(() => {
    analytics.switchMode(mode);
  }, [mode]);

  const countryCode = commonInit?.result?.GEO;
  useEffect(() => {
    if (isBoxed) return;
    if (typeof countryCode === 'string') {
      gtmInstance.setCountry(countryCode);
    }
  }, [countryCode, isBoxed]);

  // Установка языка в аналитику
  useAnalyticsLanguage(isBoxed);

  // Keep-alive серверной сессии для boxed-версии СИБУРа (конкурентные лицензии, SLIDES-3935).
  // Короткий TTL сессии есть только на boxed-деплое, поэтому пингуем только там и только авторизованных.
  useSessionPing(isAuthenticated && isBoxed);

  useEffect(() => {
    if (isAuthenticated && userInit) {
      const emailNotifyResult = checkAndSetEmailNotify();
      setEmailNotify(emailNotifyResult);
      setIsFirstTimeEmailNotify(emailNotifyResult); // Установить флаг первого показа
    }
  }, [isAuthenticated, userInit, checkAndSetEmailNotify]);

  function updateData(callback: (() => void) | undefined) {
    refetch().then(() => {
      callback?.();
    });
  }

  function logoutHandler() {
    Sentry.setUser(null);
    logout()
      .unwrap()
      .then(() => {
        navigate(route_path.main);
      });
  }

  function clearUserCtx() {
    setUserCtx(undefined);
  }

  // Ручной показ модалки подтверждения email (из header меню)
  function showManualEmailNotify() {
    setEmailNotify(true);
    setIsFirstTimeEmailNotify(false); // Это ручной показ, не первый раз
  }

  const { accounts, addAccountId } = usePersistedAccountsStore();

  // 🎯 OAuth Analytics - кастомный хук для надежной отправки
  useOAuthAnalytics(userCtx?.id);

  useEffect(() => {
    const userId = userCtx?.id;
    if (userId && !accounts.has(String(userId))) {
      addAccountId(String(userId));
    }
  }, [accounts, addAccountId, userCtx?.id]);

  function clearConfirmNotifyAndClose() {
    setSendConfirmNotify(false);
    navigate(location.pathname);
  }

  // Show modal for successful email confirmation
  useEffect(() => {
    if (window.location.search.includes('email-confirmed=True')) {
      setSendConfirmNotify(true);
    }
  }, []);

  useEffect(() => {
    const pendingMessage = localStorage.getItem('pendingWelcomeMessage');
    if (pendingMessage && localStorage.getItem('hasSeenWelcomeMessage') !== 'true') {
      setWelcomeMessage(pendingMessage);
      setShowWelcomeModal(true);
      localStorage.removeItem('pendingWelcomeMessage');
    }
    const pendingButtonText = localStorage.getItem('pendingWelcomeMessageButtonText');
    if (pendingButtonText) {
      setButtonText(pendingButtonText);
      localStorage.removeItem('pendingWelcomeMessageButtonText');
    }
  }, [location.pathname]);

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
    localStorage.setItem('hasSeenWelcomeMessage', 'true');
  };

  useEffect(() => {
    if (userInit?.result?.user_ctx?.id) {
      const currentUserId = cookieUtils.get(COOKIE_KEY.userId);
      const receivedUserId = userInit.result.user_ctx.id;
      if (currentUserId !== String(receivedUserId)) {
        cookieUtils.set(COOKIE_KEY.userId, String(receivedUserId));
      }
    }
  }, [userInit?.result.user_ctx.id, location.pathname, isSuccess, navigate]);

  useEffect(() => {
    if (isAuthChecked && !isAuthenticated && !isAuthCheckLoading) {
      // Удаляем языковой префикс для проверки защищенных маршрутов
      const pathWithoutLang = removeLanguagePrefix(location.pathname);
      const firstSegment = pathWithoutLang.split('/').filter(Boolean)[0];

      if (PROTECTED_ROUTE_LIST.includes(firstSegment as (typeof PROTECTED_ROUTE_LIST)[number])) {
        const next = encodeURIComponent(location.pathname);
        //!! Important
        const isLogout =
          next === encodeURIComponent('/logout') || next === encodeURIComponent('/logout/');

        // Используем addLanguagePrefix для редиректа на /register/ с учетом текущего языка
        const currentLanguage = i18n.language;
        const localizedRegisterPath = addLanguagePrefix(route_path.register, currentLanguage);

        if (!isLogout) {
          navigate(`${localizedRegisterPath}?next=${next}`, { replace: true });
        } else {
          navigate(localizedRegisterPath);
        }
      }
    }
  }, [dispatch, isAuthenticated, isAuthCheckLoading, location.pathname, navigate, isAuthChecked]);

  useEffect(() => {
    if (userInit?.result?.user_ctx) {
      setUserCtx(userInit.result.user_ctx);
    } else {
      setUserCtx(undefined);
    }
  }, [userInit]);

  const isWorkspacePath = [
    route_path.workspaceCreate,
    route_path.workspaceSetupStyle,
    route_path.workspaceUploadDraft,
  ].some((path) => location.pathname.includes(path));

  useEffect(() => {
    document.body.style.backgroundColor = isWorkspacePath ? '#191B20' : '#202020';
    document.documentElement.style.backgroundColor = isWorkspacePath ? '#191B20' : '#202020';
  }, [isWorkspacePath]);

  // Диагностика: лог текущих флагов загрузки перед рендером
  // console.debug('[CommonOutlet] render flags', {
  //   path: location.pathname,
  //   isAuthenticated,
  //   isCommonInitLoading,
  //   hasCommonInit: !!commonInit,
  //   isUserInitLoading,
  //   hasUserInit: !!userInit,
  //   isAuthCheckLoading,
  // });

  return (
    <>
      <NewVersionBanner />
      <Outlet
        context={
          {
            user_ctx: userCtx,
            logout: logoutHandler,
            clearUserCtx,
            context: userInit?.result,
            commonContext: commonInit?.result,
            setShowSettings,
            updateData,
            setEmailNotify: showManualEmailNotify,
            setSendConfirmNotify,
          } satisfies CommonOutletContext
        }
      />
      {!WITHOUT_FOOTER_LOCATIONS.includes(
        location.pathname.split('/')[1] as (typeof WITHOUT_FOOTER_LOCATIONS)[number],
      ) &&
      !(isAuthPage && isCommonInitLoading) &&
      !isBoxed ? (
        <Footer commonContext={commonInit?.result} />
      ) : (
        <></>
      )}
      <div className="absolute z-[151] bottom-[-500px]">
        <AnimatePresence>
          {showWelcomeModal && welcomeMessage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="z-[50]"
            >
              <WelcomeMessageModal
                message={welcomeMessage}
                isOpen={showWelcomeModal}
                onClose={handleCloseWelcomeModal}
                buttonText={buttonText}
              />
            </motion.div>
          )}
          {paywallPopupState.type && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="z-[50]"
            >
              <UpgradePopup
                variant={
                  ((paywallPopupState.type
                    ? POPUP_VARIANT_MAP[paywallPopupState.type]
                    : 'fallback') || 'fallback') as UpgradePopupVariant
                }
                popupName={paywallPopupState.type ?? undefined}
                contextMessageKey={paywallPopupState.contextMessageKey}
                isOpen={!!paywallPopupState.type}
                onClose={resetPaywallPopup}
                onClickDownload={() => {
                  // Для download_trial вариантов
                  if (typeof paywallPopupState.callBack === 'function') {
                    paywallPopupState.callBack();
                  }
                  resetPaywallPopup();
                }}
                userHasSubscription={isProPlan(userInit?.result)}
                currentTokensCount={
                  (userInit?.result?.product_balance?.included_ai_token_count || 0) +
                  (userInit?.result?.product_balance?.purchased_ai_token_count || 0)
                }
                onShareByLink={() => {
                  useSharingDialogData.getState().setIsOpen(true);
                  if (paywallPopupState.type) {
                    resetPaywallPopup();
                  }
                }}
              />
            </motion.div>
          )}
          {emailNotify && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ResendConfirmAccountNotify
                close={() => {
                  setEmailNotify(false);
                }}
                linkSended={isFirstTimeEmailNotify} // true для первого показа (кнопка "OK"), false для ручного (кнопка "Resend")
              />
            </motion.div>
          )}
          {sendConfirmNotify && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <AccountConfirmNotify close={clearConfirmNotifyAndClose} />
            </motion.div>
          )}
          {userInit && (
            <>
              <SettingsDialog
                isOpen={showSettings}
                setOpen={setShowSettings}
                experimental_features={
                  userInit?.result?.user_options &&
                  userInit?.result?.user_options.experimental_features
                    ? userInit?.result?.user_options.experimental_features
                    : false
                }
                isShowExperimental={false}
              />

              <PaymentsDialog />
            </>
          )}
        </AnimatePresence>
        {/* <UpgradePopupTest /> */}
      </div>
      {isDevMode && <SSERecoveryDebugPanel />}
    </>
  );
};
